import React from "react";
import { Button } from "@material-ui/core";

import Playlist from "./Playlist";
import { camelotToKeyMode } from "../../utils/harmonic";
import { useScAnalysis } from "../SoundCloud/ScAnalysis";

// Parse a SoundCloud date string into the Spotify album.release_date shape so a
// SoundCloud track reuses the exact same release-date formatting as Spotify
// rows: "YYYY-MM..." → "YYYY-MM-01", else a bare year. Returns
// { date, precision } or null.
function scReleaseDate(track) {
  const raw =
    track.display_date || track.release_date || track.created_at || null;
  if (!raw) return null;
  const m = /(\d{4})[-/](\d{1,2})/.exec(String(raw));
  if (m) {
    const mon = String(parseInt(m[2], 10)).padStart(2, "0");
    return { date: `${m[1]}-${mon}-01`, precision: "month" };
  }
  const y = /(\d{4})/.exec(String(raw));
  return y ? { date: y[1], precision: "year" } : null;
}

// A thin adapter that renders the combined (Spotify + SoundCloud) track view
// through the REAL Playlist component, so it is pixel-identical to the Spotify
// view. SoundCloud tracks are shaped like Spotify playlist items (carrying a
// __source tag + __scRaw so Playlist/Row can guard the few combined-only
// extras), and their keys are synthesized from our own analysis as it fills in.
let CombinedPlaylist = (props) => {
  const {
    open,
    onClose,
    title,
    spotifyItems,
    spotifyFeatures,
    scTracks,
    token,
    userId,
    updatePlayer,
    onPlaySoundcloud,
    onAddToSet,
    onOpenSet,
    setCount,
    bottomInset,
  } = props;

  // The app-level shared queue (so analysis continues after close + a global
  // indicator shows progress).
  const { analysis, enqueueAll, setBpmOverride } = useScAnalysis();

  // Manual BPM correction for a SoundCloud row (track.id === urn here). Stable
  // identity so memoized Rows don't churn.
  const onOverrideBpm = React.useCallback(
    (urn, bpm) => setBpmOverride(urn, bpm),
    [setBpmOverride]
  );

  // Auto-analyze the SoundCloud tracks on open so their key/BPM fill in live.
  React.useEffect(() => {
    if (open && scTracks && scTracks.length) enqueueAll(scTracks);
  }, [open, scTracks, enqueueAll]);

  // Spotify items pass through (tagged); SoundCloud tracks are reshaped into the
  // Spotify playlist-item shape Playlist/Row consume.
  const items = React.useMemo(() => {
    const sp = (spotifyItems || []).map((it) => ({ ...it, __source: "spotify" }));
    const sc = (scTracks || []).map((t) => {
      const rel = scReleaseDate(t);
      return {
        added_at: null,
        __source: "soundcloud",
        __scRaw: t,
        track: {
          id: t.urn || String(t.id),
          name: t.title,
          artists: [{ name: (t.user && t.user.username) || "" }],
          album: {
            images: t.artwork_url ? [{ url: t.artwork_url }] : [],
            release_date: rel ? rel.date : undefined,
            release_date_precision: rel ? rel.precision : undefined,
          },
          uri: null,
          duration_ms: t.duration || null,
          external_urls: {},
        },
      };
    });
    return [...sp, ...sc];
  }, [spotifyItems, scTracks]);

  // Spotify audio-features pass through; SoundCloud keys are synthesized from
  // our analysis (Camelot → Spotify-style key/mode) keyed by the same synthetic
  // track id used above, so getKey resolves them. Rows without analysis yet are
  // omitted and render as "N/A" until their result arrives.
  const playlistKeys = React.useMemo(() => {
    const scKeys = (scTracks || [])
      .map((t) => {
        const urn = t.urn || String(t.id);
        const a = analysis[urn];
        if (!a || !a.camelot) return null;
        const km = camelotToKeyMode(a.camelot);
        if (!km) return null;
        // A manual BPM correction (bpmOverride) wins over our detected bpm.
        const tempo = a.bpmOverride != null ? a.bpmOverride : a.bpm;
        return { id: urn, key: km.key, mode: km.mode, tempo, energy: null };
      })
      .filter(Boolean);
    return [...(spotifyFeatures || []), ...scKeys];
  }, [spotifyFeatures, scTracks, analysis]);

  // SoundCloud analysis progress for the header status slot: how many of the
  // SoundCloud tracks have reached a terminal result (key, "Set", or error).
  const scStatus = React.useMemo(() => {
    const scList = scTracks || [];
    if (!scList.length) return null;
    let done = 0;
    const failed = [];
    scList.forEach((t) => {
      const a = analysis[t.urn || String(t.id)];
      if (a && (a.camelot || a.isLikelySet || a.error || a.unavailable)) {
        done += 1;
        // Only genuine failures are retryable; "unavailable" verdicts
        // (SoundCloud-only / removed) can never be analyzed, so retrying is moot.
        if (a.error) failed.push(t);
      }
    });
    return { total: scList.length, done, failed };
  }, [scTracks, analysis]);

  const statusWrap = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "rgba(255,255,255,0.75)",
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    marginRight: 8,
  };
  // Live progress is shown by the GLOBAL floating indicator now (one source of
  // truth), so the header only surfaces the "couldn't analyze + Retry"
  // affordance once analysis has finished and some retryable failures remain.
  let combinedStatus = null;
  if (scStatus && scStatus.done >= scStatus.total && scStatus.failed.length) {
    combinedStatus = (
      <span style={statusWrap}>
        {scStatus.failed.length} couldn't analyze
        <Button
          size="small"
          onClick={() => enqueueAll(scStatus.failed)}
          style={{ color: "#ff5500", textTransform: "none", minWidth: 0 }}
        >
          Retry
        </Button>
      </span>
    );
  }

  // Per-row SoundCloud status for the Key-column marker: still analyzing,
  // failed, or flagged as a Set. Analyzed tracks (have a key) get no marker —
  // their real key shows. Keyed by the same id as the combined item's track.id.
  const scStatusById = React.useMemo(() => {
    const m = {};
    (scTracks || []).forEach((t) => {
      const urn = t.urn || String(t.id);
      const a = analysis[urn];
      // Analyzed tracks keep their real key; preview-derived ones get a marker.
      if (a && a.camelot) {
        if (a.preview) m[urn] = "preview";
        return;
      }
      if (a && a.isLikelySet) m[urn] = "set";
      else if (a && a.unavailable) m[urn] = "unavailable";
      else if (a && a.error) m[urn] = "failed";
      else m[urn] = "loading";
    });
    return m;
  }, [scTracks, analysis]);

  // Per-row chord loop (urn -> ["Db","Ab","Eb","Bbm"]) for the Key-cell reveal.
  const chordsById = React.useMemo(() => {
    const m = {};
    (scTracks || []).forEach((t) => {
      const urn = t.urn || String(t.id);
      const a = analysis[urn];
      if (a && a.chords && a.chords.length) m[urn] = a.chords;
    });
    return m;
  }, [scTracks, analysis]);

  // Mount the Playlist only while open, so it initializes its internal
  // searchItems from the FULL track list (the data is ready by the time `open`
  // flips true). Keeping it mounted-but-closed left searchItems stuck on the
  // empty initial list — the table showed nothing until "clear filters".
  if (!open) return null;

  return (
    <Playlist
      open={open}
      handlePlaylistClose={onClose}
      playlistName={title}
      playlistId="__combined__"
      playlistOwnerId={null}
      playlist={items}
      playlistKeys={playlistKeys}
      token={token}
      userId={userId}
      updatePlayer={updatePlayer}
      onPlaySoundcloud={onPlaySoundcloud}
      onAddToSet={onAddToSet}
      onOpenSet={onOpenSet}
      setCount={setCount}
      combinedStatus={combinedStatus}
      scStatusById={scStatusById}
      chordsById={chordsById}
      bottomInset={bottomInset}
      onOverrideBpm={onOverrideBpm}
    />
  );
};

export default CombinedPlaylist;
